import type { MapartProject, ProjectSummary } from './project';

const DB_NAME = 'vektor.mapart';
const DB_VERSION = 1;
const STORE = 'projects';
const LAST_OPENED_KEY = 'vektor.mapart.lastOpened';

// Projects hold pasted screenshots, which blow past the localStorage quota, so the documents live in
// IndexedDB and only the pointer to the last opened one is a preference.
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = work(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
      }),
  );
}

export async function saveProject(project: MapartProject): Promise<void> {
  await run('readwrite', (store) => store.put(project) as IDBRequest<IDBValidKey>);
}

export async function loadProject(id: string): Promise<MapartProject | null> {
  const found = await run<MapartProject | undefined>('readonly', (store) => store.get(id));
  return found ?? null;
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const all = await run<MapartProject[]>('readonly', (store) => store.getAll());
  return all
    .map(({ id, name, updatedAt }) => ({ id, name, updatedAt }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function deleteProject(id: string): Promise<void> {
  await run('readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
  if (lastOpenedId() === id) rememberLastOpened(null);
}

export function lastOpenedId(): string | null {
  try {
    return localStorage.getItem(LAST_OPENED_KEY);
  } catch {
    return null;
  }
}

export function rememberLastOpened(id: string | null) {
  try {
    if (id) localStorage.setItem(LAST_OPENED_KEY, id);
    else localStorage.removeItem(LAST_OPENED_KEY);
  } catch {
    /* private mode or storage disabled */
  }
}
